import React, { useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack as ArrowBackIcon, SupportAgent as SupportIcon } from '@mui/icons-material';

const openHelp = () => document.dispatchEvent(new Event('krillou:open-help'));

const SECTIONS = ['what', 'types', 'stripe', 'manage', 'contact'];

const CookiesPage = () => {
  const { t } = useTranslation('legal');
  const theme = useTheme();
  const titleRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header with Breadcrumb */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: 2,
        px: 3,
        borderRadius: 2,
        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
        color: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        mb: 4,
        gap: 1.5
      }}>
        <Breadcrumbs sx={{ 
          mb: 0,
          '& .MuiBreadcrumbs-separator': { color: 'rgba(255, 255, 255, 0.7)' }
        }}>
          <Link
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'white', '&:hover': { opacity: 0.8 } }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            {t('common:navigation.home', 'Accueil')}
          </Link>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('cookies.title')}</Typography>
        </Breadcrumbs>
        <Typography
          ref={titleRef}
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, color: 'white' }}
          tabIndex={-1}
        >
          {t('cookies.title')}
        </Typography>
      </Box>

      {/* Intro */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 5,
          bgcolor: alpha(theme.palette.warning.main, 0.06),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
          borderRadius: 2,
        }}
      >
        <Typography sx={{ lineHeight: 1.8 }}>{t('cookies.intro')}</Typography>
      </Paper>

      {/* Sections */}
      {SECTIONS.map((key) => {
        const paragraphs = t(`cookies.sections.${key}.paragraphs`, { returnObjects: true });
        return (
          <Box key={key} component="section" sx={{ mb: 5 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              {t(`cookies.sections.${key}.title`)}
            </Typography>

            {Array.isArray(paragraphs) &&
              paragraphs.map((p, i) => (
                <Typography key={i} sx={{ mb: 2, lineHeight: 1.85, color: 'text.secondary' }}>
                  {p}
                </Typography>
              ))}

            {key === 'contact' && (
              <Button
                variant="contained"
                startIcon={<SupportIcon />}
                onClick={openHelp}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('contactBtn')}
              </Button>
            )}

            <Divider sx={{ mt: 3 }} />
          </Box>
        );
      })}

      {/* Cross-link to privacy */}
      <Box sx={{ mt: 2, pb: 2 }}>
        <Typography color="text.secondary" variant="body2">
          {t('common:footer.privacyPolicy', 'Politique de confidentialité')} :{' '}
          <Link component={RouterLink} to="/privacy" underline="hover">
            {t('common:footer.privacyPolicy', 'Politique de confidentialité')}
          </Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default CookiesPage;
